/* global Config */

// Vendor Libs
import React, {useEffect, useCallback, useState} from 'react';
import {Alert} from '@material-ui/lab';
import MaterialTable from 'material-table';
import axios from 'axios';

// Material UI
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@material-ui/core';
import AddBox from '@material-ui/icons/AddBox';
import ArrowDownward from '@material-ui/icons/ArrowDownward';
import Check from '@material-ui/icons/Check';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import Clear from '@material-ui/icons/Clear';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import Edit from '@material-ui/icons/Edit';
import FilterList from '@material-ui/icons/FilterList';
import FirstPage from '@material-ui/icons/FirstPage';
import LastPage from '@material-ui/icons/LastPage';
import Remove from '@material-ui/icons/Remove';
import SaveAlt from '@material-ui/icons/SaveAlt';
import Search from '@material-ui/icons/Search';
import ViewColumn from '@material-ui/icons/ViewColumn';

// Components
import downloadCSV from '../hooks/downloadCSV';
import useFetch from '../hooks/useFetch';

const tableIcons = {
  Add: AddBox,
  Check: Check,
  Clear: Clear,
  Delete: DeleteOutline,
  DetailPanel: ChevronRight,
  Edit: Edit,
  Export: SaveAlt,
  Filter: FilterList,
  FirstPage: FirstPage,
  LastPage: LastPage,
  NextPage: ChevronRight,
  PreviousPage: ChevronLeft,
  ResetSearch: Clear,
  Search: Search,
  SortArrow: ArrowDownward,
  ThirdStateCheck: Remove,
  ViewColumn: ViewColumn,
};

const URL = '/api';

const api = axios.create({
  baseURL: `${URL}/swrollout/v1/whitelist`,
});

interface IRow {
  receiverId: string;
  model: string;
  type: string;
  description: string;
}

const EditComponent = (validation: any) => (props: any) => {
  const error = validation(props.value);
  return (
    <TextField
      error={error}
      helperText={error}
      inputProps={{
        onChange: (e) => props.onChange(e.currentTarget.value),
        value: props.value,
      }}
    />
  );
};

const UploadAlertDialog = ({results, handleClose}: any) => {
  return (
    <Dialog open={!!results} onClose={handleClose}>
      <DialogTitle>Import Complete</DialogTitle>
      <DialogContent>{(results || []).length} items not uploaded. See Results for Info.</DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        <Button onClick={() => downloadCSV(results)}>Download CSV</Button>
      </DialogActions>
    </Dialog>
  );
};

const modelValidation = (models: string[]) => (value: string = '') => {
  return models.indexOf(value) === -1 ? 'Invalid Model id' : null;
};

const recieverValidation = (value: string = '') => {
  return value.match(/^R[0-9]{10}$/) ? null : 'Invalid Reciever id';
};

export default () => {
  const [isUploading, setIsUploading] = useState(false);
  const {data: types} = useFetch(`${URL}/swrollout/v1/whitelist/types`);
  const {data: models = []} = useFetch(`${URL}/swrollout/v1/whitelist/models`);
  const [rows, setRows] = React.useState<IRow[]>([]);
  const [error, setError] = React.useState<any>(null);
  const [results, setResults] = useState();
  const [appData, openConfirmDialog] = useState({open: false, rows: []});

  const getRows = useCallback(() => {
    fetch(`${URL}/swrollout/v1/whitelist`)
      .then(res => res.json())
      .then(data => {
        setRows(data.receivers);
      });
  }, [setRows]);
  useEffect(() => {
    getRows();
  }, [getRows]);

  const columns: any = [
    {
      title: 'Reciever id',
      field: 'receiverId',
      editComponent: EditComponent(recieverValidation),
      editable: 'onAdd',
    },
    {
      title: 'Model',
      field: 'model',
      editable: 'always',
      lookup: (models || []).reduce((curr: {[index: string]: string}, model: string) => {
        curr[model] = model;
        return curr;
      }, {}),
    },
    {
      title: 'Type',
      field: 'type',
      editable: 'always',
      lookup: (types || []).reduce((curr: {[index: string]: string}, type: string) => {
        curr[type] = type;
        return curr;
      }, {}),
    },
    {
      title: 'Description',
      width: '50%',
      field: 'description',
      filtering: false,
      editable: 'always',
    },
  ];

  const upload = async (e: React.ChangeEvent<HTMLInputElement>, type: string) => {
    setIsUploading(true);
    const data = new FormData();
    data.set('action', type);
    data.append('file', e.target.files![0]);
    e.target.value = '';
    const {data: results} = await api.post('/upload', data, {
      headers: {'Content-Type': 'multipart/form-data'},
    });
    setResults(results);
    getRows();
    setIsUploading(false);
  };

  return (
    <>
      <UploadAlertDialog results={results} handleClose={() => setResults(undefined)} />
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        <label htmlFor="add">
          <Button disabled={isUploading} style={{margin: '1rem'}} variant="contained" color="primary" component="span">
            {isUploading ? 'Uploading...' : 'Import Recievers'}
          </Button>
          <input
            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            style={{display: 'none'}}
            id="add"
            type="file"
            onChange={(e) => upload(e, 'ADD')}
          />
        </label>
      </div>
      {error && (
        <Alert style={{marginBottom: '1rem'}} severity="error">
          {error}
        </Alert>
      )}

      <MaterialTable
        editable={{
          isEditable: () => true,
          isDeletable: () => true,
          onRowUpdate: async (newData, oldData) => {
            if (!oldData) {
              return;
            }
            if (modelValidation(models)(newData.model) || recieverValidation(newData.receiverId)) {
              throw new Error('Validation Error');
            }
            await api.put(``, newData);
            setRows(rows.map((row) => (oldData.receiverId === row.receiverId ? newData : row)));
          },
          onRowDelete: async (oldData) => {
            await api.delete(`/${oldData.receiverId}`);
            setRows(rows.filter(({receiverId}) => oldData.receiverId !== receiverId));
          },
          onRowAdd: (newData) => {
            return new Promise((res, rej) => {
              if (modelValidation(models)(newData.model) || recieverValidation(newData.receiverId)) {
                rej();
              }
              api
                .post('', newData)
                .then(({data: row}) => {
                  setRows([row, ...rows]);
                  setError(null);
                  res();
                })
                .catch((e) => {
                  setError(e.response.data);
                  rej();
                });
            });
          },
        }}
        actions={[
          {
            tooltip: 'Remove All Selected',
            icon: () => <DeleteOutline/>,
            onClick: async (_, rawRows) =>
              openConfirmDialog({
                open: true,
                rows: Array.isArray(rawRows) ? rawRows : [rawRows],
              }),
          },
        ]}
        options={{
          showTitle: false,
          search: true,
          selection: true,
          actionsColumnIndex: -1,
          filtering: true,
          addRowPosition: 'first',
        }}
        icons={tableIcons}
        columns={columns}
        data={rows}
      />
      <Dialog open={appData.open}>
        <DialogTitle id="form-dialog-title">Delete Rows</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {appData.rows.length
              ? `Are you sure you want to delete ${appData.rows.length} rows?`
              : `There are no rows to delete`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => openConfirmDialog({open: false, rows: []})}>
            Cancel
          </Button>
          {appData.rows.length && (
            <Button
              color="primary"
              onClick={async () => {
                const ids = appData.rows.map(({receiverId}) => receiverId);
                await api.delete('', {
                  data: {
                    receivers: ids,
                  },
                });
                getRows();
                openConfirmDialog({open: false, rows: []});
              }}
            >
              Delete
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
